import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { AppNotification, Page } from '@/api/types'
import type { NotificationCategory } from '@/api/types/notifications'

export type NotificationsPage = Page<AppNotification> & {
  unread: number
  /** unread per filter group (only groups with unread items) */
  unreadByCategory?: Partial<Record<NotificationCategory, number>>
}

export type NotificationsQuery = {
  unread?: boolean
  category?: NotificationCategory
  page?: number
  pageSize?: number
}

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (q: NotificationsQuery = {}) => [...notificationKeys.all, 'list', q] as const,
}

export const notificationsQuery = (q: NotificationsQuery = {}) =>
  queryOptions({
    queryKey: notificationKeys.list(q),
    queryFn: ({ signal }) => api.get<NotificationsPage>('/notifications', { query: q, signal }),
  })

/** Notifications, refreshed every 30 s so the bell count stays current. */
export function useNotifications(q: NotificationsQuery = {}) {
  return useQuery({ ...notificationsQuery(q), refetchInterval: 30_000 })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
    meta: { silent: true },
  })
}

/** Marks every unread notification read, or only those of one filter group. */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (category: NotificationCategory | void) =>
      api.post('/notifications/read-all', {}, { query: { category: category || undefined } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  })
}
